import { create } from "zustand";

import { useOrganisationStore } from "@/stores/OrganisationStore";
import {
  AuthUser,
  clearStoredUser,
  fetchCurrentUser,
  readStoredUser,
  writeStoredUser,
} from "@/Utils/Auth/authSession";
import { loggerService, LogTag } from "@/Utils/LoggerService";

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Sets the authenticated user and persists it (non-sensitive) for reload. */
  setUser: (user: AuthUser) => void;
  /** Clears the user. Tokens are cleared server-side via /auth/logout. */
  clearUser: () => void;
  /** Revalidates the session against the httpOnly cookie via /users/me. */
  hydrate: () => Promise<void>;
}

const storedUser = readStoredUser();

export const useAuthStore = create<AuthState>((set) => ({
  user: storedUser,
  isAuthenticated: Boolean(storedUser),

  setUser: (user) => {
    loggerService.info(LogTag.AUTH, "User authenticated", user.email);
    writeStoredUser(user);
    set({ user, isAuthenticated: true });
  },

  clearUser: () => {
    loggerService.info(LogTag.AUTH, "User logged out");
    clearStoredUser();
    set({ user: null, isAuthenticated: false });
  },

  hydrate: async () => {
    const user = await fetchCurrentUser();

    if (user) {
      // /users/me always answers with the user's default organisation. When
      // the user switched to another organisation, keep that choice instead
      // of silently resetting the context on reload.
      const activeOrganisation = useOrganisationStore.getState().organisation;
      const hydratedUser =
        user.organisation &&
        activeOrganisation &&
        activeOrganisation.org_id !== user.organisation.org_id
          ? {
              ...user,
              organisation: {
                org_id: activeOrganisation.org_id,
                name: activeOrganisation.name,
                slug: activeOrganisation.slug,
                role: activeOrganisation.role,
              },
            }
          : user;

      writeStoredUser(hydratedUser);
      set({ user: hydratedUser, isAuthenticated: true });
    } else {
      clearStoredUser();
      set({ user: null, isAuthenticated: false });
    }
  },
}));
